import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Check, X, Search, Upload, Download } from "lucide-react";

const DIVISIONS = [
  { key: "ramp", label: "Ramp" },
  { key: "ops", label: "Ops" },
  { key: "cargo", label: "Cargo" },
  { key: "provisioning", label: "Provisioning" },
];

const TEMPLATE_HEADERS = [
  "Rank", "EID", "Name", "PhoneNumber", "FT/PT",
  "WN SLI Seniority Date", "FL ATL Seniority Date", "Dual Seniority Date", "Hire Date",
];

const EMPTY_NEW = { name: "", eid: "", phone: "", rank: "", type: "" };

export function ManageAgents({ readOnly = false }) {
  const [division, setDivision] = useState("ramp");
  const [search, setSearch] = useState("");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newAgent, setNewAgent] = useState(EMPTY_NEW);
  const [editing, setEditing] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ division, limit: "1500" });
    if (search.trim()) params.set("q", search.trim());
    api.get(`/agents?${params.toString()}`)
      .then((r) => setAgents(r.data))
      .catch(() => setAgents([]))
      .finally(() => setLoading(false));
  }, [division, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const addAgent = async () => {
    const name = newAgent.name.trim();
    if (!name) { toast.error("Agent name is required"); return; }
    try {
      const body = { division, name, eid: newAgent.eid.trim(), phone: newAgent.phone.trim(), type: newAgent.type.trim() };
      if (newAgent.rank !== "") body.rank = Number(newAgent.rank);
      await api.post("/agents", body);
      toast.success(`Added ${name}`);
      setNewAgent(EMPTY_NEW);
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add agent");
    }
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setEditVals({ name: a.name || "", eid: a.eid || "", phone: a.phone || "" });
  };

  const saveEdit = async (a) => {
    const name = (editVals.name || "").trim();
    if (!name) { toast.error("Name cannot be empty"); return; }
    try {
      await api.put(`/agents/${a.id}`, { name, eid: editVals.eid.trim(), phone: editVals.phone.trim() });
      toast.success("Agent updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update agent");
    }
  };

  const count = useMemo(() => agents.length, [agents]);

  const divisionLabel = DIVISIONS.find((d) => d.key === division)?.label || division;

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(xlsx|xlsm|csv)$/i.test(f.name)) {
      toast.error("Please choose a .xlsx or .csv file");
      return;
    }
    setPendingFile(f);
    setPreview(null);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/agents/import/preview?division=${division}`, fd);
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the file");
      setPendingFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const closeImport = () => {
    if (importing) return;
    setPendingFile(null);
    setPreview(null);
  };

  const runImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post(`/agents/import?division=${division}`, fd);
      toast.success(`Imported ${data.imported} agents into ${divisionLabel}`);
      setPendingFile(null);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "agent_roster_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden mb-6" data-testid="manage-agents">
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Manage Agents</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {readOnly
              ? "View agents on each roster. Only the Master Admin can add, rename or import agents."
              : "Add new agents, rename existing agents, or import an updated roster (Excel/CSV) per department."}
          </p>
        </div>
        {!readOnly && !adding && (
          <div className="flex items-center gap-2 shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xlsm,.csv"
              onChange={onFilePicked}
              className="hidden"
              data-testid="manage-agents-import-input"
            />
            <Button variant="outline" data-testid="manage-agents-import-btn" onClick={() => fileInputRef.current?.click()}
              className="rounded-sm h-9">
              <Upload className="w-4 h-4 mr-1" /> Import Excel
            </Button>
            <Button data-testid="manage-agents-add-toggle" onClick={() => setAdding(true)}
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-9">
              <Plus className="w-4 h-4 mr-1" /> Add Agent
            </Button>
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger className="rounded-sm h-9 w-[180px]" data-testid="manage-agents-division">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIVISIONS.map((d) => (
                <SelectItem key={d.key} value={d.key} data-testid={`manage-agents-division-${d.key}`}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="manage-agents-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, EID or phone…" className="rounded-sm h-9 pl-9" />
          </div>
          <span className="text-xs text-muted-foreground" data-testid="manage-agents-count">{count} agent{count !== 1 ? "s" : ""}</span>
          {!readOnly && (
            <Button variant="ghost" size="sm" data-testid="manage-agents-template" onClick={downloadTemplate}
              className="rounded-sm h-8 text-xs text-muted-foreground ml-auto">
              <Download className="w-3.5 h-3.5 mr-1" /> Template
            </Button>
          )}
        </div>

        {!readOnly && adding && (
          <div className="border border-border rounded-sm p-4 bg-secondary/40" data-testid="manage-agents-add-form">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              <Input data-testid="new-agent-name" value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} placeholder="Full name *" className="rounded-sm h-9 sm:col-span-2" />
              <Input data-testid="new-agent-eid" value={newAgent.eid} onChange={(e) => setNewAgent({ ...newAgent, eid: e.target.value })} placeholder="EID" className="rounded-sm h-9" />
              <Input data-testid="new-agent-phone" value={newAgent.phone} onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })} placeholder="Phone" className="rounded-sm h-9" />
              <Input data-testid="new-agent-rank" value={newAgent.rank} onChange={(e) => setNewAgent({ ...newAgent, rank: e.target.value })} placeholder="Rank #" type="number" className="rounded-sm h-9" />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button data-testid="new-agent-save" onClick={addAgent} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-9">
                <Check className="w-4 h-4 mr-1" /> Save agent
              </Button>
              <Button variant="ghost" className="rounded-sm h-9" onClick={() => { setAdding(false); setNewAgent(EMPTY_NEW); }}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="border border-border rounded-sm divide-y divide-border max-h-[420px] overflow-y-auto">
          {loading && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && agents.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">No agents found.</div>
          )}
          {!loading && agents.map((a) => (
            <div key={a.id} data-testid={`agent-row-${a.id}`} className="px-4 py-2.5">
              {editing === a.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-center">
                  <Input autoFocus data-testid="agent-edit-name" value={editVals.name} onChange={(e) => setEditVals({ ...editVals, name: e.target.value })} className="rounded-sm h-8" placeholder="Name" />
                  <Input data-testid="agent-edit-eid" value={editVals.eid} onChange={(e) => setEditVals({ ...editVals, eid: e.target.value })} className="rounded-sm h-8" placeholder="EID" />
                  <Input data-testid="agent-edit-phone" value={editVals.phone} onChange={(e) => setEditVals({ ...editVals, phone: e.target.value })} className="rounded-sm h-8" placeholder="Phone" />
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8 rounded-sm text-green-600" data-testid="agent-edit-save" onClick={() => saveEdit(a)}><Check className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 rounded-sm text-muted-foreground" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {a.rank != null && <span className="text-muted-foreground mr-2">#{a.rank}</span>}
                      {a.name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.eid ? `EID ${a.eid}` : "No EID"}{a.phone ? ` · ${a.phone}` : ""}{a.type ? ` · ${a.type}` : ""}
                    </div>
                  </div>
                  {!readOnly && (
                    <Button size="sm" variant="ghost" className="h-8 rounded-sm shrink-0" data-testid={`agent-edit-${a.id}`} onClick={() => startEdit(a)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!pendingFile} onOpenChange={(o) => { if (!o) closeImport(); }}>
        <AlertDialogContent className="rounded-sm bg-card max-w-lg" data-testid="manage-agents-import-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the {divisionLabel} roster?</AlertDialogTitle>
            <AlertDialogDescription>
              {previewing
                ? "Reading the file…"
                : preview
                  ? `This will replace all ${preview.current} current ${divisionLabel} agent${preview.current !== 1 ? "s" : ""} with ${preview.count} agent${preview.count !== 1 ? "s" : ""} from ${preview.filename}. Grievances are not affected.`
                  : `Replace the ${divisionLabel} roster with the uploaded file.`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {previewing && (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="manage-agents-preview-loading">Parsing file…</div>
          )}

          {!previewing && preview && (
            <div className="space-y-3" data-testid="manage-agents-preview">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-head text-2xl font-extrabold" data-testid="preview-new-count">{preview.count}</span>
                  <span className="text-muted-foreground text-xs uppercase tracking-wider">new rows</span>
                </div>
                <span className="text-muted-foreground">→ replaces</span>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-head text-2xl font-extrabold text-muted-foreground">{preview.current}</span>
                  <span className="text-muted-foreground text-xs uppercase tracking-wider">current</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Sample (first {preview.sample.length})</div>
                <div className="border border-border rounded-sm divide-y divide-border max-h-[220px] overflow-y-auto">
                  {preview.sample.map((s, i) => (
                    <div key={i} data-testid={`preview-row-${i}`} className="px-3 py-1.5 flex items-center justify-between gap-3 text-sm">
                      <span className="truncate">
                        {s.rank != null && <span className="text-muted-foreground mr-1.5">#{s.rank}</span>}{s.name}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {s.eid ? `EID ${s.eid}` : "No EID"}{s.hire_date ? ` · hired ${s.hire_date}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Check the names and EIDs look right before replacing.</p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={importing}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-primary hover:bg-primary/90" data-testid="manage-agents-import-run"
              disabled={importing || previewing || !preview} onClick={(e) => { e.preventDefault(); runImport(); }}>
              {importing ? "Importing…" : preview ? `Replace with ${preview.count} agents` : "Import"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
